import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import SpmConstants from 'sg-spm-constants';
import SgVehicleUse from 'sg-spm-vehicle-use';
import SgVehicleMileage from 'sg-spm-vehicle-mileage';
import { SgDateUtil } from 'saga-modules';
import { useValidation } from 'gw-portals-validation-react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import metadata from './SgDriverAdditionalQuestions.metadata.json5';
import messages from './SgDriverAdditionalQuestions.messages';

function SgDriverAdditionalQuestions(props) {
    const {
        id,
        modelVM,
        updateData,
        driverIdRemove,
        onValidate: onValidateParent,
        showErrors,
    } = props;

    const commonProps = {
        modelVM,
        updateData,
        showErrors,
        messageParams: {
            vehicle: SpmConstants.CAR
        }
    };

    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);

    const [selectedMainDriverId, setSelectedMainDriverId] = useState(undefined);

    useEffect(() => {
        if (onValidateParent) {
            const isValid = isComponentValid && !!selectedMainDriverId;
            onValidateParent(isValid, id);
        }
    }, [id, isComponentValid, onValidateParent, selectedMainDriverId]);

    useEffect(() => {
        setSelectedMainDriverId(undefined);
        // if there will be only one driver on policy after driver removal
        if (driverIdRemove && modelVM.lobData.privateMotor.coverables.spmDrivers.value.length === 2) {
            const policyHolder = modelVM.lobData.privateMotor.coverables.spmDrivers.value.find((driver) => driver.isPolicyHolder);
            setSelectedMainDriverId(policyHolder.publicID);
        }
    }, [driverIdRemove, setSelectedMainDriverId]);

    const getInfo = useCallback((driver) => {
        const prefix = driver.person.readOnlyPrefix || driver.person.prefix;
        if (!prefix || !driver.person || !driver.person.dateOfBirth) {
            return '';
        }
        const personPrefix = prefix[0].toUpperCase() + prefix.slice(1);
        return `${personPrefix} ${driver.person.firstName} ${driver.person.lastName} [${SgDateUtil.toStringShort(driver.person.dateOfBirth)}]`;
    }, []);

    const mainDriverAvailableValues = useMemo(() => {
        const drivers = _.cloneDeep(modelVM.lobData.privateMotor.coverables.spmDrivers.value);
        if (driverIdRemove !== -1) {
            _.remove(drivers, (driver) => driver.publicID && driver.publicID === driverIdRemove);
        }
        return drivers.map((driver) => {
            return {
                name: getInfo(driver),
                id: driver.publicID || driver.tempID
            };
        });
    }, [modelVM, driverIdRemove]);

    const setMainDriver = useCallback((driverId) => {
        setSelectedMainDriverId(driverId);
        const modelCopy = _.clone(modelVM);
        modelCopy.lobData.privateMotor.coverables.spmDrivers.value.forEach((driver) => {
            const newValue = driver.publicID === driverId || driver.tempID === driverId;
            _.set(driver, 'isMainUserOfVehicle', newValue);
        });
        updateData(modelCopy);
    }, [modelVM, updateData]);

    const showMainDriverSelect = useMemo(() => {
        const numberOfDrivers = modelVM.lobData.privateMotor.coverables.spmDrivers.value.length;
        return driverIdRemove ? numberOfDrivers > 2 : numberOfDrivers > 1;
    }, [driverIdRemove, modelVM.lobData.privateMotor.coverables.spmDrivers.value.length]);

    const overrides = {
        vehicleMileageComponent: {
            ...commonProps
        },
        vehicleUseComponent: {
            ...commonProps
        },
        mainDriver: {
            availableValues: mainDriverAvailableValues,
            onValueChange: setMainDriver,
            value: selectedMainDriverId,
            visible: showMainDriverSelect,
            validationMessages: selectedMainDriverId === undefined ? [translator(messages.MainDriverValidation)] : null,
            ...commonProps
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgVehicleMileage: SgVehicleMileage,
            SgVehicleUse: SgVehicleUse
        },
        resolveCallbackMap: {}
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            onModelChange={updateData}
            overrideProps={overrides}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default SgDriverAdditionalQuestions;
